import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Trash2, GripVertical, Plus, Image as ImageIcon, Calendar, Clock } from 'lucide-react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { isVisibleByDate } from '../../lib/dateVisibility';

/**
 * CarouselBannerEditor - Éditeur de bannières pour le bloc carousel
 * 
 * Permet de :
 * - Ajouter/supprimer des bannières
 * - Réorganiser par drag & drop
 * - Upload d'images ou URL
 * - Configurer titre, lien, visibilité
 * - Définir interval et options
 */
const CarouselBannerEditor = ({ content, onChange }) => {
  const { 
    banners = [], 
    interval = 5000,
    loop = true,
    animation = 'fade'
  } = content;

  const [visualMode, setVisualMode] = useState('url'); // 'url' ou 'upload'

  // Ajouter une nouvelle bannière
  const handleAddBanner = () => {
    const newBanner = {
      id: `banner-${Date.now()}`,
      image: '',
      title: '',
      link: '',
      order: banners.length,
      is_active: true,
      start_date: '',  // Champ date de début (format ISO 8601)
      end_date: ''     // Champ date de fin (format ISO 8601)
    };

    onChange({
      ...content,
      banners: [...banners, newBanner]
    });
  };

  // Calculer le statut de visibilité d'une bannière
  const getBannerVisibilityStatus = (banner) => {
    if (!banner.is_active) {
      return { status: 'inactive', label: 'Désactivée', color: 'bg-gray-100 text-gray-600' };
    }
    
    const hasStartDate = banner.start_date && banner.start_date.trim() !== '';
    const hasEndDate = banner.end_date && banner.end_date.trim() !== '';
    
    if (!hasStartDate && !hasEndDate) {
      return { status: 'always', label: 'Toujours visible', color: 'bg-green-100 text-green-700' };
    }
    
    const isVisible = isVisibleByDate(banner.start_date, banner.end_date);
    
    if (isVisible) {
      return { status: 'visible', label: 'Visible actuellement', color: 'bg-green-100 text-green-700' };
    }
    
    // Vérifier si c'est programmé (futur) ou expiré (passé)
    const now = new Date();
    const startDate = hasStartDate ? new Date(banner.start_date) : null;
    
    if (startDate && now < startDate) {
      return { status: 'scheduled', label: 'Programmée', color: 'bg-blue-100 text-blue-700' };
    }
    
    return { status: 'expired', label: 'Expirée', color: 'bg-orange-100 text-orange-700' };
  };

  // Supprimer une bannière
  const handleDeleteBanner = (bannerId) => {
    onChange({
      ...content,
      banners: banners.filter(b => b.id !== bannerId)
    });
  };

  // Mettre à jour une bannière
  const handleUpdateBanner = (bannerId, field, value) => {
    onChange({
      ...content,
      banners: banners.map(b => 
        b.id === bannerId ? { ...b, [field]: value } : b
      )
    });
  };

  // Réorganiser les bannières (drag & drop)
  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const items = Array.from(banners);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    // Mettre à jour les orders
    const updatedBanners = items.map((item, index) => ({
      ...item,
      order: index
    }));

    onChange({
      ...content,
      banners: updatedBanners
    });
  };

  // Upload d'image
  const handleImageUpload = async (bannerId, e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await fetch('/api/upload', {
        method: 'POST',
        body: formData,
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      const data = await response.json();
      if (data.url) {
        handleUpdateBanner(bannerId, 'image', data.url);
      }
    } catch (error) {
      console.error('Erreur upload:', error);
      alert('Erreur lors de l\'upload de l\'image');
    }
  };

  return (
    <div className="space-y-6">
      {/* Configuration globale */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="font-semibold text-blue-900 mb-3">Configuration du Carousel</h4>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Interval */}
          <div className="space-y-2">
            <Label htmlFor="carousel-interval">Interval (ms)</Label>
            <Input
              id="carousel-interval"
              type="number"
              min="1000"
              step="500"
              value={interval}
              onChange={(e) => onChange({ ...content, interval: parseInt(e.target.value) || 5000 })}
              placeholder="5000"
            />
            <p className="text-xs text-muted-foreground">
              Temps entre chaque slide (1000ms = 1s)
            </p>
          </div>

          {/* Loop */}
          <div className="space-y-2">
            <Label htmlFor="carousel-loop">Loop infini</Label>
            <div className="flex items-center gap-2 pt-2">
              <Switch
                id="carousel-loop"
                checked={loop}
                onCheckedChange={(checked) => onChange({ ...content, loop: checked })}
              />
              <span className="text-sm text-muted-foreground">
                {loop ? 'Activé' : 'Désactivé'}
              </span>
            </div>
          </div>

          {/* Animation */}
          <div className="space-y-2">
            <Label htmlFor="carousel-animation">Type d'animation</Label>
            <Select
              value={animation}
              onValueChange={(value) => onChange({ ...content, animation: value })}
            >
              <SelectTrigger id="carousel-animation">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="fade">Fondu (Fade)</SelectItem>
                <SelectItem value="slide">Glissement (Slide)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Liste des bannières */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <h4 className="font-semibold">Bannières ({banners.length})</h4>
          <Button
            type="button"
            size="sm"
            onClick={handleAddBanner}
            className="gap-2"
          >
            <Plus className="h-4 w-4" />
            Ajouter une bannière
          </Button>
        </div>

        {banners.length === 0 ? (
          <div className="bg-gray-50 border border-dashed border-gray-300 rounded-lg p-8 text-center">
            <ImageIcon className="h-12 w-12 text-gray-400 mx-auto mb-2" />
            <p className="text-gray-500">Aucune bannière. Cliquez sur "Ajouter" pour commencer.</p>
          </div>
        ) : (
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="carousel-banners">
              {(provided) => (
                <div
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                  className="space-y-2"
                >
                  {banners.map((banner, index) => (
                    <Draggable key={banner.id} draggableId={banner.id} index={index}>
                      {(provided) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          className="bg-white border rounded-lg p-4"
                        >
                          <div className="flex items-start gap-3">
                            {/* Drag Handle */}
                            <div
                              {...provided.dragHandleProps}
                              className="mt-2 text-gray-400 hover:text-gray-600 cursor-grab"
                            >
                              <GripVertical className="h-5 w-5" />
                            </div>

                            {/* Contenu */}
                            <div className="flex-1 space-y-3">
                              {/* Header bannière avec statut de visibilité */}
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <span className="text-sm font-semibold text-gray-700">
                                    Bannière #{index + 1}
                                  </span>
                                  {/* Badge de statut de visibilité */}
                                  {(() => {
                                    const status = getBannerVisibilityStatus(banner);
                                    return (
                                      <span className={`text-xs px-2 py-0.5 rounded-full ${status.color}`}>
                                        {status.label}
                                      </span>
                                    );
                                  })()}
                                </div>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDeleteBanner(banner.id)}
                                  className="text-destructive hover:text-destructive"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>

                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                {/* Image */}
                                <div className="space-y-2">
                                  <Label>Image *</Label>
                                  <div className="flex gap-2">
                                    <Button
                                      type="button"
                                      variant={visualMode === 'url' ? 'default' : 'outline'}
                                      size="sm"
                                      onClick={() => setVisualMode('url')}
                                    >
                                      URL
                                    </Button>
                                    <Button
                                      type="button"
                                      variant={visualMode === 'upload' ? 'default' : 'outline'}
                                      size="sm"
                                      onClick={() => setVisualMode('upload')}
                                    >
                                      Upload
                                    </Button>
                                  </div>
                                  
                                  {visualMode === 'url' ? (
                                    <Input
                                      type="url"
                                      value={banner.image}
                                      onChange={(e) => handleUpdateBanner(banner.id, 'image', e.target.value)}
                                      placeholder="https://..."
                                    />
                                  ) : (
                                    <Input
                                      type="file"
                                      accept="image/*"
                                      onChange={(e) => handleImageUpload(banner.id, e)}
                                    />
                                  )}

                                  {banner.image && (
                                    <img
                                      src={banner.image}
                                      alt="Prévisualisation"
                                      className="w-full h-20 object-cover rounded border"
                                    />
                                  )}
                                </div>

                                {/* Titre */}
                                <div className="space-y-2">
                                  <Label>Titre</Label>
                                  <Input
                                    type="text"
                                    value={banner.title}
                                    onChange={(e) => handleUpdateBanner(banner.id, 'title', e.target.value)}
                                    placeholder="Titre de la bannière"
                                  />
                                </div>

                                {/* Lien */}
                                <div className="space-y-2">
                                  <Label>Lien (optionnel)</Label>
                                  <Input
                                    type="text"
                                    value={banner.link}
                                    onChange={(e) => handleUpdateBanner(banner.id, 'link', e.target.value)}
                                    placeholder="/dashboard/nouveautes ou https://..."
                                  />
                                  <p className="text-xs text-muted-foreground">
                                    Lien interne (/dashboard/...) ou externe (https://...)
                                  </p>
                                </div>

                                {/* Active */}
                                <div className="space-y-2">
                                  <Label>Visibilité</Label>
                                  <div className="flex items-center gap-2 pt-1">
                                    <Switch
                                      checked={banner.is_active}
                                      onCheckedChange={(checked) => handleUpdateBanner(banner.id, 'is_active', checked)}
                                    />
                                    <span className="text-sm text-muted-foreground">
                                      {banner.is_active ? 'Visible' : 'Cachée'}
                                    </span>
                                  </div>
                                </div>
                              </div>

                              {/* Planification de visibilité (dates) */}
                              {banner.is_active && (
                                <div className="border-t pt-3 mt-3">
                                  <div className="flex items-center gap-2 mb-2">
                                    <Calendar className="h-4 w-4 text-blue-600" />
                                    <Label className="text-sm font-semibold">
                                      Planification (optionnel)
                                    </Label>
                                  </div>
                                  
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    {/* Date de début */}
                                    <div className="space-y-2">
                                      <Label htmlFor={`start-${banner.id}`} className="text-xs">
                                        Date de début
                                      </Label>
                                      <Input
                                        id={`start-${banner.id}`}
                                        type="datetime-local"
                                        value={banner.start_date || ''}
                                        onChange={(e) => handleUpdateBanner(banner.id, 'start_date', e.target.value)}
                                        className="text-sm"
                                      />
                                      <p className="text-xs text-muted-foreground">
                                        {banner.start_date 
                                          ? `Visible à partir du ${new Date(banner.start_date).toLocaleString('fr-FR')}`
                                          : 'Visible immédiatement'
                                        }
                                      </p>
                                    </div>

                                    {/* Date de fin */}
                                    <div className="space-y-2">
                                      <Label htmlFor={`end-${banner.id}`} className="text-xs">
                                        Date de fin
                                      </Label>
                                      <Input
                                        id={`end-${banner.id}`}
                                        type="datetime-local"
                                        value={banner.end_date || ''}
                                        onChange={(e) => handleUpdateBanner(banner.id, 'end_date', e.target.value)}
                                        className="text-sm"
                                      />
                                      <p className="text-xs text-muted-foreground">
                                        {banner.end_date 
                                          ? `Visible jusqu'au ${new Date(banner.end_date).toLocaleString('fr-FR')}`
                                          : 'Visible indéfiniment'
                                        }
                                      </p>
                                    </div>
                                  </div>

                                  {/* Validation endDate >= startDate */}
                                  {banner.start_date && banner.end_date && new Date(banner.end_date) < new Date(banner.start_date) && (
                                    <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700">
                                      ⚠️ La date de fin doit être postérieure à la date de début
                                    </div>
                                  )}

                                  {/* Indicateur visuel de programmation */}
                                  {(banner.start_date || banner.end_date) && (
                                    <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded text-xs text-blue-700 flex items-center gap-2">
                                      <Clock className="h-3 w-3" />
                                      Bannière programmée
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        )}
      </div>

      {/* Aide */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-sm text-gray-600">
        <p className="font-semibold mb-2">💡 Conseils :</p>
        <ul className="list-disc list-inside space-y-1">
          <li>Images recommandées : 1920x768px (ratio 5:2)</li>
          <li>Utilisez le drag & drop pour réorganiser les bannières</li>
          <li>Les bannières inactives ne seront pas affichées</li>
          <li>Si aucune bannière active, le carousel sera masqué</li>
        </ul>
      </div>
    </div>
  );
};

export default CarouselBannerEditor;
